import React from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useParams } from 'react-router-dom'

import { getSlugFromPrettyUrl } from '@data/util/urlHelpers'

import { Screen } from '@newComponents/Screen'
import { Grid, SponsorsSection } from '@newComponents'
import { Card } from '@newComponents/Card'
import { PresentationMetadata } from '@newComponents/Presentation/PresentationMetadata'
import { PresentationVideoPlayer } from '@newComponents/Presentation/PresentationVideoPlayer'
import { YStack, Separator } from 'tamagui'

import { PresentationInfo } from './PresentationInfo'
import PresentationEvents from './PresentationEvents'
import PresentationGuilds from './PresentationGuilds'
import { PresentationSimilar } from './PresentationSimilar'

import { PresentationScreenQuery } from './__generated__/PresentationScreenQuery.graphql'

const PresentationScreenInner = () => {
  const params = useParams()
  const data = useLazyLoadQuery<PresentationScreenQuery>(
    graphql`
      query PresentationScreenQuery($slug: String!) {
        presentationBySlug(slug: $slug) {
          videoSourceUrl
          ...PresentationMetadata_presentation
          ...PresentationVideoPlayerNew_presentation
          ...PresentationInfo_presentation
          ...PresentationEvents_presentation
          ...PresentationGuilds_presentation
          ...PresentationSimilar_presentation
        }

        ...SponsorsSection_query
      }
    `,
    { slug: getSlugFromPrettyUrl(params.presentationId) }
  )

  if (!data.presentationBySlug) {
    return null
  }

  const p = data.presentationBySlug

  return (
    <>
      <PresentationMetadata presentation={p} />

      {/* Mobile: single column */}
      <YStack gap='$8' $gtSm={{ display: 'none' }}>
        {!!p.videoSourceUrl && (
          <Card padding={0} overflow='hidden'>
            <PresentationVideoPlayer presentation={p} />
          </Card>
        )}

        <Card>
          <PresentationInfo presentation={p} />
        </Card>

        <PresentationEvents presentation={p} />

        <PresentationGuilds presentation={p} />

        <PresentationSimilar presentation={p} />

        <Separator borderColor='$borderColor' />

        <SponsorsSection query={data} padding='$4' />
      </YStack>

      {/* Desktop: 2 columns */}
      <Grid gap='$8' display='none' $gtSm={{ display: 'flex' }}>
        <Grid.Column flexGrow={2} $gtMd={{ flexGrow: 3 }}>
          <YStack gap='$8'>
            {!!p.videoSourceUrl && (
              <Card padding={0} overflow='hidden'>
                <PresentationVideoPlayer presentation={p} />
              </Card>
            )}

            <Card>
              <PresentationInfo presentation={p} />
            </Card>

            <PresentationEvents presentation={p} />

            <PresentationGuilds presentation={p} />

            <PresentationSimilar presentation={p} />
          </YStack>
        </Grid.Column>

        <Grid.Column flexGrow={1}>
          <YStack gap='$8' style={{ position: 'sticky', top: 96 }}>
            <SponsorsSection query={data} />
          </YStack>
        </Grid.Column>
      </Grid>
    </>
  )
}

export const PresentationScreen = () => {
  return (
    <Screen gap='$6'>
      <PresentationScreenInner />
    </Screen>
  )
}

export default PresentationScreen
